/** Trigger wash-down lance, authored lying down because this is the interactable end of the reel. */
import * as THREE from 'three';
import { surface } from '../materials';
import { seatOnOrigin } from '../origin';
import type { AssetDefinition } from '../types';

function build(): THREE.Group {
  const group = new THREE.Group();
  group.name = 'hose-nozzle';
  const steel = surface('metal', 0x899394);
  const brass = surface('metal', 0xa7824d);
  const rubber = surface('organic', 0x202728);

  // A tapered cylinder rotated onto Z has its narrower end facing +Z: water and
  // the asset's forward convention therefore agree instead of merely looking right.
  const barrel = new THREE.Mesh(new THREE.CylinderGeometry(0.017, 0.028, 0.09, 8), brass);
  barrel.name = 'tapered-nozzle-barrel';
  barrel.rotation.x = Math.PI / 2;
  barrel.position.set(0, 0.05, 0.125);
  barrel.castShadow = true;
  group.add(barrel);

  const collar = new THREE.Mesh(new THREE.CylinderGeometry(0.03, 0.03, 0.03, 8), steel);
  collar.name = 'barrel-collar';
  collar.rotation.x = Math.PI / 2;
  collar.position.set(0, 0.05, 0.065);
  group.add(collar);

  const grip = new THREE.Mesh(new THREE.BoxGeometry(0.035, 0.055, 0.026), rubber);
  grip.name = 'pistol-grip';
  grip.rotation.x = 0.35;
  grip.position.set(0, 0.032, 0.055);
  grip.castShadow = true;
  group.add(grip);

  const trigger = new THREE.Mesh(new THREE.BoxGeometry(0.009, 0.027, 0.009), steel);
  trigger.name = 'trigger-lever';
  trigger.rotation.x = -0.42;
  trigger.position.set(0, 0.045, 0.09);
  group.add(trigger);

  // The hose spends its length in an S rather than a straight line, fitting the
  // specified 0.34 m kit envelope while still looking limp and connected.
  const hoseCurve = new THREE.CatmullRomCurve3([
    new THREE.Vector3(0, 0.024, 0.05),
    new THREE.Vector3(0.037, 0.019, -0.015),
    new THREE.Vector3(-0.037, 0.014, -0.09),
    new THREE.Vector3(0.018, 0.01, -0.166),
  ]);
  const hose = new THREE.Mesh(new THREE.TubeGeometry(hoseCurve, 12, 0.009, 6, false), rubber);
  hose.name = 'limp-hose-tail';
  hose.castShadow = true;
  group.add(hose);

  // Authored from a convenient face outward, then seated on the contract's
  // origin. See `seatOnOrigin` for why five assets in this batch needed it.
  seatOnOrigin(group);

  return group;
}

export const hoseNozzleAsset: AssetDefinition = {
  id: 'hose-nozzle',
  label: 'Wash-down nozzle',
  description: 'Floor-lying trigger lance facing +Z, with a tapered barrel and limp attached hose.',
  footprint: { width: 0.09, height: 0.08, length: 0.34 },
  create: build,
};
